import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { buildQuoteLineItems } from '~components/Project/QuotePdf/buildQuoteLineItems';
import {
  COMBINED_RATE_MARKER,
  formatRateWithMarker,
} from '~components/Project/QuotePdf/utils';
import { Nullable } from '~types/Nullable';
import { formatPercentage } from '~utils/utilFunctions';

interface QuotePreviewDialogProps {
  currencyFormatter: (value: number | string) => string;
  isOpen: boolean;
  lineItems: ReturnType<typeof buildQuoteLineItems>;
  onClose: () => void;
  taxRate: Nullable<number>;
}

const headerCellSx = { fontWeight: 600, whiteSpace: 'nowrap' };
const subtotalCellSx = { fontWeight: 600 };
const noteSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray3,
  display: 'block',
  mt: 1,
});

export default function QuotePreviewDialog({
  currencyFormatter,
  isOpen,
  lineItems,
  onClose,
  taxRate,
}: QuotePreviewDialogProps) {
  const {
    addOnRows,
    addOnSubtotal,
    canSubtotalDelivered,
    combinedAnyFreightRate,
    deliveredPriceRows,
    deliveredPricingIncludesFreight,
    deliveredSubtotal,
    freightRows,
    freightSubtotal,
    hasAnyAddOnAmount,
    hasAnyFreightLineTotal,
    hasAnyMaterialLineTotal,
    hasComputableTax,
    hasDeliveredPricing,
    hasFreight,
    hasMaterials,
    materialRows,
    materialSubtotal,
    shouldCombineMaterialAndHaul,
    taxAmount,
    taxBasisSuffix,
  } = lineItems;
  const showsItemizedTables = !shouldCombineMaterialAndHaul;

  return (
    <Dialog isOpen={isOpen} maxWidth="md" onClose={onClose}>
      <DialogHeader onClose={onClose}>{t('project.quote_preview.title')}</DialogHeader>

      <DialogContent>
        {hasDeliveredPricing && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.equipment_type')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.material_name')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>{t('project.quote_pdf.source')}</TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.quantity')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>{t('project.quote_pdf.rate')}</TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.sales_price')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveredPriceRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.equipmentType}</TableCell>
                    <TableCell>{row.material}</TableCell>
                    <TableCell>{row.source}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>
                      {formatRateWithMarker(row.rate, row.rateIsCombined)}
                    </TableCell>
                    <TableCell>{row.salesPrice}</TableCell>
                  </TableRow>
                ))}
                {canSubtotalDelivered && (
                  <TableRow>
                    <TableCell colSpan={5} sx={subtotalCellSx}>
                      {t('project.quote_pdf.delivered_subtotal')}
                    </TableCell>
                    <TableCell sx={subtotalCellSx}>
                      {currencyFormatter(deliveredSubtotal.toNumber())}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {combinedAnyFreightRate && (
              <Typography sx={noteSx} variant="caption">
                {`${COMBINED_RATE_MARKER} ${t('project.quote_pdf.combined_rates_note')}`}
              </Typography>
            )}
            {deliveredPricingIncludesFreight && (
              <Typography sx={noteSx} variant="caption">
                {t('project.quote_pdf.freight_rates_note')}
              </Typography>
            )}
          </TableContainer>
        )}

        {showsItemizedTables && hasMaterials && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.material_name')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>{t('project.quote_pdf.site')}</TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.quantity')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>{t('project.quote_pdf.unit')}</TableCell>
                  <TableCell sx={headerCellSx}>{t('project.quote_pdf.rate')}</TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('project.quote_pdf.line_total')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materialRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell>{row.rate}</TableCell>
                    <TableCell>{row.lineTotal}</TableCell>
                  </TableRow>
                ))}
                {hasAnyMaterialLineTotal && (
                  <TableRow>
                    <TableCell colSpan={5} sx={subtotalCellSx}>
                      {t('project.quote_pdf.material_subtotal')}
                    </TableCell>
                    <TableCell sx={subtotalCellSx}>
                      {currencyFormatter(materialSubtotal.toNumber())}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showsItemizedTables && hasFreight && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }} variant="body2">
              {t('project.quote_pdf.freight')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.equipment_type')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.description')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.route')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.quantity')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>{t('project.quote_pdf.rate')}</TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.line_total')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {freightRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.equipType}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.route}</TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>
                        {formatRateWithMarker(row.rate, row.rateIsCombined)}
                      </TableCell>
                      <TableCell>{row.lineTotal}</TableCell>
                    </TableRow>
                  ))}
                  {hasAnyFreightLineTotal && (
                    <TableRow>
                      <TableCell colSpan={5} sx={subtotalCellSx}>
                        {t('project.quote_pdf.freight_subtotal')}
                      </TableCell>
                      <TableCell sx={subtotalCellSx}>
                        {currencyFormatter(freightSubtotal.toNumber())}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {combinedAnyFreightRate && (
              <Typography sx={noteSx} variant="caption">
                {`${COMBINED_RATE_MARKER} ${t('project.quote_pdf.combined_rates_note')}`}
              </Typography>
            )}
            <Typography sx={noteSx} variant="caption">
              {t('project.quote_pdf.freight_rates_note')}
            </Typography>
          </Box>
        )}

        {addOnRows.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }} variant="body2">
              {t('project.quote_pdf.add_ons')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.add_on')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.qty_or_percent')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.basis')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('project.quote_pdf.amount')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addOnRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.quantityOrPercent}</TableCell>
                      <TableCell>{row.basis}</TableCell>
                      <TableCell>{row.amount}</TableCell>
                    </TableRow>
                  ))}
                  {hasAnyAddOnAmount && (
                    <TableRow>
                      <TableCell colSpan={3} sx={subtotalCellSx}>
                        {t('project.quote_pdf.add_on_subtotal')}
                      </TableCell>
                      <TableCell sx={subtotalCellSx}>
                        {currencyFormatter(addOnSubtotal.toNumber())}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {hasComputableTax && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              mt: 3,
            }}
          >
            <Typography variant="body2">
              {`${t('project.quote_pdf.tax')} ${formatPercentage(taxRate)}${taxBasisSuffix}`}
            </Typography>
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              {currencyFormatter(taxAmount.toNumber())}
            </Typography>
          </Box>
        )}

        <Typography
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray3, mt: 3 })}
          variant="caption"
        >
          {t('project.quote_preview.note')}
        </Typography>
      </DialogContent>

      <DialogActions>
        <SmallButton color="secondary" onClick={onClose} variant="outlined">
          {t('actions.close')}
        </SmallButton>
      </DialogActions>
    </Dialog>
  );
}
